/**
 * Moving between terms while one is still running.
 *
 * Two shapes, and the difference between them is *when*:
 *
 *   - **An upgrade** — a longer term — starts today. The term running now ends
 *     today with it, and whatever was paid for and not used comes off the
 *     price of the new one.
 *   - **A downgrade** — a shorter term — is queued. The term running now is
 *     untouched and runs to its last day; the new one starts the day after.
 *     Nothing is credited, and nothing is refunded for the difference.
 *
 * **Both are paid for at the checkout**, and that is the one thing the designs
 * for this flow do not say. They were drawn for the mandate, where a downgrade
 * changed what a card would be charged next time and so cost nothing on the
 * day it was asked for. There is no mandate now and nothing bills itself: a
 * queued term is a term that has been bought, so a downgrade is charged in
 * full today and starts later. See `endpoints.ts`, where `amount_due` is named
 * as what gets charged whatever kind of purchase it is.
 *
 * **It is all on the quote already.** `POST /billing/prepaid/quote/` answers
 * `purchase_kind`, the `current_term` being replaced or queued behind, the
 * dates the new term would cover and the `credit` block behind anything taken
 * off, and `prepaidService` maps the lot into `PrepaidQuote`. Nothing here
 * asks the server anything, nothing here works out a price, and nothing new
 * goes on the wire for any of it.
 *
 * The one thing the quote cannot say is how long the term being left ran for.
 * It names it by plan code — `PRO_3M` — and the months behind that code are
 * the price list's, which is why `plans` is asked for alongside. A code the
 * list has never heard of still changes perfectly well; it is simply named
 * "your current plan" rather than by a length this build cannot stand behind.
 */

import { monthsLabel, planTermLabel } from "./pricing";
import type { SubscriptionPlan } from "./types/billing.types";
import type { PrepaidQuote } from "./types/prepaid.types";

/** The two moves this reads. A renewal is the same plan again and a conversion
 *  is a buy-out of an internal term; neither is a change of length, and
 *  neither draws any of what this describes. */
export type TermChangeKind = "UPGRADE" | "DOWNGRADE";

/** A change of term as the three screens of it read one. */
export interface TermChangeView {
  kind: TermChangeKind;
  /**
   * Whether the new term waits its turn rather than starting today.
   *
   * True on a downgrade and false on an upgrade, and carried as its own fact
   * rather than re-derived from `kind` at every call site — it is what decides
   * whether the summary draws a handover at all.
   */
  queued: boolean;
  /** The replaced term's length, or `null` where the price list cannot name
   *  the plan code it was bought on. */
  fromMonths: number | null;
  /** `"12 month plan"`, or `"your current plan"` for the case above. */
  fromLabel: string;
  toMonths: number;
  /** `"3 month plan"`. */
  toLabel: string;
  /** What the unused days of a term cut short took off, in rupees, exactly as
   *  the server subtracted it. Always `0` on a downgrade: a term that runs to
   *  its own last day has no unused days to credit. */
  credit: number;
  /** How many whole IST days that figure was counted from. */
  unusedDays: number;
  /** The last day the term being kept covers, on a queued change — `null` on
   *  an upgrade, which ends it today instead. ISO day. */
  currentEndsOn: string | null;
  /** The day the new term starts: today on an upgrade, the day after the
   *  current term ends on a downgrade. ISO day. */
  startsOn: string;
  /** The new term's own last day. ISO day. */
  endsOn: string;
  /** What the screens head themselves with: `"Downgrade to 3 months"`. */
  headline: string;
}

/**
 * The change of term behind a quote, or `null` where it is not one.
 *
 * Only the two lengths. A `RENEWAL` is the same term again — there is no
 * "from" to draw and nothing moves — and an `INTERNAL_CONVERSION` is a buy-out
 * of an Internal Billing term rather than a change of plan length, so calling
 * either one of these on screen would name it something it is not. The credit
 * a conversion does carry is drawn off the price breakdown like any other,
 * without this.
 *
 * The kind is the server's and is read rather than inferred. `purchaseKindFor`
 * guesses one for the price list, which has no quote to go on; this has the
 * quote, so there is nothing left to guess — and the two can disagree, which
 * is why the checkout re-reads it here rather than trusting the word on the
 * button that opened it.
 */
export const termChangeView = (
  quote: PrepaidQuote | null | undefined,
  plans: SubscriptionPlan[]
): TermChangeView | null => {
  if (!quote) return null;
  if (quote.purchaseKind !== "UPGRADE" && quote.purchaseKind !== "DOWNGRADE") {
    return null;
  }

  const kind: TermChangeKind = quote.purchaseKind;
  const queued = kind === "DOWNGRADE";
  const replacing = quote.currentTerm;
  const held = replacing
    ? plans.find((plan) => plan.code === replacing.planCode) ?? null
    : null;
  const toMonths = quote.price.months;

  return {
    kind,
    queued,
    fromMonths: held?.months ?? null,
    fromLabel: held ? planTermLabel(held.months) : "your current plan",
    toMonths,
    toLabel: planTermLabel(toMonths),
    // The figure off the breakdown rather than off the credit block: that is
    // the one the server actually subtracted, and the two are the same number
    // until the day they are not.
    credit: quote.price.credit,
    unusedDays: quote.credit?.unusedDays ?? 0,
    // Only where there is a term still to run. On an upgrade the old term ends
    // today, so there is no "until" to print and no handover to draw.
    currentEndsOn: queued ? replacing?.endsOn ?? null : null,
    startsOn: quote.startsOn,
    endsOn: quote.endsOn,
    headline: `${queued ? "Downgrade" : "Upgrade"} to ${monthsLabel(toMonths)}`,
  };
};

export default termChangeView;
